import type { Types as CornerstoneTypes } from '@cornerstonejs/core';

type Point2 = CornerstoneTypes.Point2;

/** Cornerstone's PlanarFreehandROI edit state, plus what a staged reshape keeps. */
export interface StagedEditData {
  prevCanvasPoints: Point2[];
  editCanvasPoints: Point2[];
  fusedCanvasPoints?: Point2[];
  startCrossingIndex?: number;
  editIndex: number;
  snapIndex?: number;
  annotation?: unknown;
  /** The contour after the segments already closed in this drag. */
  stagedCanvasPoints?: Point2[];
  /** Their drag path, still drawn dotted. */
  stagedDragPoints?: Point2[];
}

/** Cornerstone's PlanarFreehandROI draw state. */
export interface StagedDrawData {
  canvasPoints: Point2[];
  polylineIndex: number;
  /** Set when an existing open trace is extended from its end. */
  extensionFrom?: number;
  stagedDragPoints?: Point2[];
}

/**
 * Cornerstone closes a reshape each time the drag crosses back over the
 * contour and starts a new one from there. The next edit, with the result
 * held in it instead of written to the annotation.
 */
export function stageEdit(data: StagedEditData): StagedEditData {
  const { fusedCanvasPoints, editCanvasPoints, stagedDragPoints = [] } = data;
  if (!fusedCanvasPoints) return data;
  return {
    prevCanvasPoints: fusedCanvasPoints,
    editCanvasPoints: [editCanvasPoints[editCanvasPoints.length - 1]],
    // Releasing right on the crossing (a touch on the edge) still applies it.
    fusedCanvasPoints,
    startCrossingIndex: undefined,
    editIndex: 0,
    snapIndex: undefined,
    annotation: data.annotation,
    stagedCanvasPoints: fusedCanvasPoints,
    stagedDragPoints: [...stagedDragPoints, ...editCanvasPoints],
  };
}

/** Draw state that keeps extending a reshaped open trace from its end. */
export function endEditDrawData(
  fusedCanvasPoints: Point2[],
  data: StagedEditData
): StagedDrawData {
  return {
    canvasPoints: fusedCanvasPoints,
    polylineIndex: fusedCanvasPoints.length - 1,
    extensionFrom: fusedCanvasPoints.length - data.editCanvasPoints.length,
    stagedDragPoints: data.stagedDragPoints,
  };
}
